import React, { useState, useEffect, useRef, createContext, useContext, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context";
import { X, Star, Sparkles, Lock, UserPlus, LogIn, Clock, Timer, Zap } from "lucide-react";
import GoogleSignInButton from "./GoogleSignInButton";

const GUEST_TIME_LIMIT = 5 * 60; // 5 minutes in seconds
const LESSON_EXTENSION_SECONDS = 120;  // +2 min per lesson start
const MAX_EXTENSIONS = 3;              // Hard cap so sessions can't run forever
const GUEST_PROGRESS_KEY = "isaiah_guest_progress";

const initialProgress = () => ({
  stars: 0,
  games_played: 0,
  lessons_started: 0,
  extensions_used: 0,
  started_at: new Date().toISOString(),
  activity_log: [], // [{type, at}]
});

const loadProgress = () => {
  try {
    const raw = localStorage.getItem(GUEST_PROGRESS_KEY);
    return raw ? { ...initialProgress(), ...JSON.parse(raw) } : initialProgress();
  } catch (_) {
    return initialProgress();
  }
};

const saveProgress = (p) => {
  try { localStorage.setItem(GUEST_PROGRESS_KEY, JSON.stringify(p)); } catch (_) {}
};

export const clearGuestProgress = () => {
  try { localStorage.removeItem(GUEST_PROGRESS_KEY); } catch (_) {}
};

const GuestContext = createContext({});
export const useGuest = () => useContext(GuestContext);

export const GuestProvider = ({ children }) => {
  const { user } = useApp();
  const isGuest = user?.isDemo === true;
  const [timeLeft, setTimeLeft] = useState(GUEST_TIME_LIMIT);
  const [expired, setExpired] = useState(false);
  const [showGateModal, setShowGateModal] = useState(false);
  const [gateMessage, setGateMessage] = useState("");
  const [showSessionSummary, setShowSessionSummary] = useState(false);
  const [progress, setProgress] = useState(loadProgress());
  const [extensionToast, setExtensionToast] = useState(null); // { message, addedSec }
  const timerRef = useRef(null);

  // Start/stop timer based on guest status
  useEffect(() => {
    if (!isGuest) {
      if (timerRef.current) clearInterval(timerRef.current);
      return;
    }
    const saved = sessionStorage.getItem("guest_time_left");
    if (saved) setTimeLeft(parseInt(saved));

    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        const next = prev - 1;
        sessionStorage.setItem("guest_time_left", String(Math.max(0, next)));
        if (next <= 0) {
          clearInterval(timerRef.current);
          setExpired(true);
          // Show Session Summary FIRST (loss-aversion conversion lever).
          // Gate modal is only shown after the user dismisses the summary.
          setShowSessionSummary(true);
          return 0;
        }
        if (next === 60) {
          setGateMessage("Just 1 minute left in your preview! Sign up now to keep your stars and save your progress.");
          setShowGateModal(true);
        }
        return next;
      });
    }, 1000);

    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [isGuest]);

  // Record guest activity, also awards extensions on lesson starts
  const trackActivity = useCallback((type, meta = {}) => {
    if (!isGuest) return;
    setProgress(prev => {
      const next = { ...prev };
      next.activity_log = [...(prev.activity_log || []), { type, at: new Date().toISOString(), ...meta }].slice(-50);
      if (type === "game_start") next.games_played = (prev.games_played || 0) + 1;
      if (type === "lesson_start") {
        next.lessons_started = (prev.lessons_started || 0) + 1;
        if ((prev.extensions_used || 0) < MAX_EXTENSIONS) {
          next.extensions_used = (prev.extensions_used || 0) + 1;
          setTimeLeft(t => {
            const nt = Math.min(t + LESSON_EXTENSION_SECONDS, GUEST_TIME_LIMIT + MAX_EXTENSIONS * LESSON_EXTENSION_SECONDS);
            sessionStorage.setItem("guest_time_left", String(nt));
            return nt;
          });
          setExtensionToast({ message: "+2 minutes added!", addedSec: LESSON_EXTENSION_SECONDS });
          setTimeout(() => setExtensionToast(null), 3500);
        }
      }
      if (type === "star_earned") next.stars = (prev.stars || 0) + (meta.amount || 1);
      saveProgress(next);
      return next;
    });
  }, [isGuest]);

  const triggerGate = useCallback((message) => {
    setGateMessage(message || "Create your free account to save your stars and continue your journey.");
    setShowGateModal(true);
  }, []);

  const formatTime = useCallback((seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  }, []);

  const forceClose = useCallback(() => {
    setShowGateModal(false);
    setShowSessionSummary(false);
    setExpired(false);
    sessionStorage.removeItem("guest_time_left");
  }, []);

  // When the 5-minute preview ends and the user dismisses the Session Summary,
  // end the guest session and send them back to the homepage logged out
  // (so they clearly see the preview is over and can start fresh / sign up).
  const dismissSessionSummary = useCallback(() => {
    setShowSessionSummary(false);
    try {
      sessionStorage.removeItem("isaiah_token");
      sessionStorage.removeItem("isaiah_user");
      localStorage.removeItem("isaiah_token");
      localStorage.removeItem("isaiah_user");
      localStorage.removeItem("iw_guest_until");
      sessionStorage.removeItem("guest_time_left");
    } catch (_) {}
    window.location.href = "/";
  }, []);

  // Memoize the context value so ONLY consumers of changed primitives re-render.
  // Without this, every 1s timer tick recreates the object and forces every
  // consumer (GuestBanner, GuestProgressChip, GuestStarMilestones, game pages)
  // to re-render, can contribute to flicker on slower devices.
  const ctxValue = useMemo(() => ({
    timeLeft, expired, formatTime, triggerGate, showGateModal, setShowGateModal, gateMessage, isGuest,
    forceClose, progress, trackActivity,
  }), [timeLeft, expired, formatTime, triggerGate, showGateModal, gateMessage, isGuest, forceClose, progress, trackActivity]);

  return (
    <GuestContext.Provider value={ctxValue}>
      {children}
      {showSessionSummary && <GuestSessionSummaryModal progress={progress} onDismiss={dismissSessionSummary} forceClose={forceClose} />}
      {showGateModal && !showSessionSummary && <GuestGateModal message={gateMessage} expired={expired} progress={progress} onClose={() => { if (!expired) setShowGateModal(false); }} forceClose={forceClose} />}
      {extensionToast && (
        <div className="below-header-fixed-tight left-1/2 -translate-x-1/2 z-[9998] animate-in fade-in slide-in-from-top-4 duration-500">
          <div className="rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-4 py-2 shadow-lg shadow-emerald-500/30 flex items-center gap-2 text-sm font-bold" data-testid="extension-toast">
            <Zap size={14} /> {extensionToast.message} <span className="opacity-75 text-xs">Keep learning!</span>
          </div>
        </div>
      )}
    </GuestContext.Provider>
  );
};

const GuestGateModal = ({ message, expired, onClose, forceClose, progress }) => {
  const navigate = useNavigate();
  const { logout } = useApp();

  const goTo = (path) => {
    // Keep guest_progress intact so the signup page can claim it after account creation.
    logout();
    forceClose();
    navigate(path);
  };

  const hasProgress = progress && (progress.stars > 0 || progress.games_played > 0 || progress.lessons_started > 0);

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center" onClick={expired ? undefined : onClose} data-testid="guest-gate-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div className="relative max-w-md w-full mx-4 rounded-3xl overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="absolute inset-0 bg-gradient-to-b from-indigo-950 via-slate-900 to-slate-950" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(99,102,241,0.15),transparent_60%)]" />
        <div className="relative p-8">
          {!expired && (
            <button onClick={onClose} className="absolute top-4 right-4 text-white/30 hover:text-white/60 transition-colors" data-testid="guest-gate-close">
              <X size={20} />
            </button>
          )}

          <div className="text-center mb-5">
            <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center shadow-lg ${expired ? "bg-gradient-to-br from-red-400 to-rose-500 shadow-red-500/20" : "bg-gradient-to-br from-amber-400 to-orange-500 shadow-amber-500/20"}`}>
              {expired ? <Timer size={32} className="text-white" /> : <Star size={32} className="text-white" fill="white" />}
            </div>
            <h2 className="text-2xl font-fredoka font-bold text-white mb-2">
              {expired ? "Preview Time's Up!" : "You're Doing Great!"}
            </h2>
            <p className="text-white/60 text-sm leading-relaxed">{message}</p>
          </div>

          {hasProgress && (
            <div className="mb-5 rounded-2xl p-3 bg-amber-500/10 border border-amber-400/25" data-testid="progress-carryover">
              <p className="text-amber-200 text-xs font-bold uppercase tracking-widest mb-2 text-center">Your preview progress</p>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div>
                  <p className="text-amber-300 font-fredoka font-bold text-lg">{progress.stars}</p>
                  <p className="text-white/50 text-[10px] uppercase">Stars</p>
                </div>
                <div>
                  <p className="text-amber-300 font-fredoka font-bold text-lg">{progress.games_played}</p>
                  <p className="text-white/50 text-[10px] uppercase">Games</p>
                </div>
                <div>
                  <p className="text-amber-300 font-fredoka font-bold text-lg">{progress.lessons_started}</p>
                  <p className="text-white/50 text-[10px] uppercase">Lessons</p>
                </div>
              </div>
              <p className="text-white/50 text-[11px] text-center mt-2">
                ✨ All of this will be <strong className="text-white">saved to your new account</strong>.
              </p>
            </div>
          )}

          <div className="space-y-3">
            <GoogleSignInButton label="Continue with Google" testId="guest-gate-google" className="!bg-white" />
            <button onClick={() => goTo("/signup")}
              className="w-full py-3.5 rounded-2xl bg-gradient-to-r from-emerald-500 to-green-600 text-white font-bold text-sm hover:from-emerald-400 hover:to-green-500 transition-all shadow-lg shadow-emerald-500/20 flex items-center justify-center gap-2"
              data-testid="guest-gate-signup">
              <UserPlus size={18} /> Create My Free Account
            </button>
            <button onClick={() => goTo("/login")}
              className="w-full py-3 rounded-2xl border border-white/10 text-white/80 font-bold text-sm hover:bg-white/5 transition-all flex items-center justify-center gap-2"
              data-testid="guest-gate-login">
              <LogIn size={16} /> I Already Have an Account
            </button>
          </div>

          <div className="mt-5 pt-4 border-t border-white/5 text-center">
            <p className="text-white/30 text-xs">Free Basic Account Forever · No credit card needed.</p>
            <div className="flex items-center justify-center gap-4 mt-3 text-white/40 text-xs">
              <span className="flex items-center gap-1"><Star size={10} className="text-amber-400" /> Save progress</span>
              <span className="flex items-center gap-1"><Sparkles size={10} className="text-purple-400" /> Earn stars</span>
              <span className="flex items-center gap-1"><Lock size={10} className="text-green-400" /> Unlock features</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const GuestSessionSummaryModal = ({ progress, onDismiss, forceClose }) => {
  const navigate = useNavigate();
  const { logout } = useApp();

  const goTo = (path) => {
    // Keep guest_progress intact so signup can claim it after account creation.
    logout();
    forceClose();
    navigate(path);
  };

  const stars = progress?.stars || 0;
  const games = progress?.games_played || 0;
  const lessons = progress?.lessons_started || 0;
  // Projected stars if they sign up (loss-aversion framing: 3x more).
  const projected = Math.max(stars * 3, 25);

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center" data-testid="guest-session-summary-modal">
      <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" />
      <div className="relative max-w-md w-full mx-4 rounded-3xl overflow-hidden animate-in fade-in zoom-in-95 duration-300">
        <div className="absolute inset-0 bg-gradient-to-b from-amber-950/90 via-slate-900 to-indigo-950" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(251,191,36,0.18),transparent_65%)]" />
        <div className="relative p-8 text-center">
          <button onClick={onDismiss} className="absolute top-4 right-4 text-white/40 hover:text-white/80 transition-colors z-10" data-testid="session-summary-close" aria-label="Close">
            <X size={22} />
          </button>
          <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 shadow-lg shadow-amber-500/30 flex items-center justify-center" style={{ animation: "float 3s ease-in-out infinite" }}>
            <Star size={40} className="text-white" fill="white" />
          </div>
          <p className="text-amber-300 text-[11px] font-bold uppercase tracking-[0.22em] mb-1">Session Summary</p>
          <h2 className="text-2xl font-fredoka font-bold text-white mb-2">You did amazing!</h2>
          <p className="text-white/65 text-sm leading-relaxed mb-5">In your 5-minute preview you collected:</p>

          <div className="grid grid-cols-3 gap-2 mb-5" data-testid="session-summary-stats">
            <div className="rounded-xl bg-amber-500/10 border border-amber-400/30 py-3">
              <p className="text-amber-300 font-fredoka font-bold text-2xl" data-testid="summary-stars">{stars}</p>
              <p className="text-white/55 text-[10px] uppercase tracking-wider">Stars</p>
            </div>
            <div className="rounded-xl bg-purple-500/10 border border-purple-400/30 py-3">
              <p className="text-purple-300 font-fredoka font-bold text-2xl" data-testid="summary-games">{games}</p>
              <p className="text-white/55 text-[10px] uppercase tracking-wider">Games</p>
            </div>
            <div className="rounded-xl bg-emerald-500/10 border border-emerald-400/30 py-3">
              <p className="text-emerald-300 font-fredoka font-bold text-2xl" data-testid="summary-lessons">{lessons}</p>
              <p className="text-white/55 text-[10px] uppercase tracking-wider">Lessons</p>
            </div>
          </div>

          <div className="rounded-2xl p-4 mb-5 bg-gradient-to-br from-emerald-500/15 to-blue-500/10 border border-emerald-400/30 text-left" data-testid="session-summary-cta-box">
            <p className="text-emerald-300 text-xs font-bold uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
              <Sparkles size={12} /> Sign up and earn 3× more
            </p>
            <p className="text-white/80 text-[13px] leading-relaxed">
              With a <strong className="text-white">free account</strong>, you'd have about <strong className="text-amber-300">{projected}+ stars</strong> by now, plus daily wheel spins, streak bonuses, and unlimited play time. <strong className="text-white">Kids love it, and kids earn even more.</strong>
            </p>
          </div>

          <div className="space-y-2.5">
            <GoogleSignInButton label="Continue with Google, Keep my stars" testId="session-summary-google" className="!bg-white" />
            <button onClick={() => goTo("/signup")}
              className="w-full py-3.5 rounded-2xl bg-gradient-to-r from-emerald-500 to-green-600 text-white font-bold text-sm hover:from-emerald-400 hover:to-green-500 transition-all shadow-lg shadow-emerald-500/30 flex items-center justify-center gap-2"
              data-testid="session-summary-signup">
              <UserPlus size={18} /> Claim my {stars > 0 ? `${stars} stars` : "free account"}
            </button>
            <button onClick={onDismiss}
              className="w-full py-2 text-white/40 hover:text-white/70 text-[11px] font-bold transition-colors"
              data-testid="session-summary-dismiss">
              Maybe later, show sign-in options
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const GuestBanner = () => {
  const { user } = useApp();
  const navigate = useNavigate();
  const { timeLeft, formatTime, expired, progress } = useGuest();
  const [dismissed, setDismissed] = useState(false);

  if (!user?.isDemo || dismissed) return null;

  const isLow = timeLeft <= 60;
  const hasProgress = progress && (progress.stars > 0 || progress.games_played > 0);

  return (
    <div className="fixed top-2 left-1/2 -translate-x-1/2 z-[60] flex items-center gap-3 sm:gap-4 px-5 sm:px-7 py-2 rounded-full bg-orange-500 shadow-lg shadow-orange-500/30 ring-1 ring-white/20" data-testid="guest-banner">
      <div className="flex items-center gap-1.5 font-mono font-bold text-sm text-white whitespace-nowrap">
        <Clock size={14} className={isLow ? "animate-pulse" : ""} />
        <span data-testid="guest-timer">{expired ? "0:00" : formatTime(timeLeft)}</span>
      </div>
      <span className="hidden sm:inline text-white text-xs font-bold uppercase tracking-wider whitespace-nowrap">Preview Mode</span>
      <span className="sm:hidden text-white text-xs font-bold uppercase tracking-wider">Guest</span>
      {hasProgress && (
        <span className="hidden md:inline-flex items-center gap-1 bg-white/20 rounded-full px-2 py-0.5 text-white text-[10px] font-bold" data-testid="guest-banner-progress">
          <Star size={10} fill="currentColor" /> {progress.stars}
        </span>
      )}
      <button onClick={() => navigate("/signup")} className="px-3 py-1 rounded-full bg-white text-orange-600 text-[11px] font-bold hover:bg-white/90 whitespace-nowrap" data-testid="guest-banner-signup">
        Sign Up Free
      </button>
      {!expired && (
        <button onClick={() => setDismissed(true)} className="text-white font-bold text-sm hover:text-white/80" data-testid="guest-banner-dismiss">
          <X size={16} strokeWidth={3} />
        </button>
      )}
    </div>
  );
};

export const useGuestGate = () => {
  const { user } = useApp();
  const guest = useGuest();
  const isGuest = user?.isDemo === true;

  const gateAction = (message) => {
    if (isGuest && guest.expired) {
      guest.triggerGate("Your preview time has ended! Create your free account to keep your progress.");
      return true;
    }
    if (isGuest) {
      guest.triggerGate(message);
      return true;
    }
    return false;
  };

  const isExpired = () => isGuest && guest.expired;

  return {
    isGuest,
    gateAction,
    isExpired,
    timeLeft: guest.timeLeft,
    formatTime: guest.formatTime,
    trackActivity: guest.trackActivity,
    progress: guest.progress,
  };
};
